// Accès offert (billing grants) — pure helpers shared by the organization
// detail card and the organizations list badge. Statuses come from backoffice
// (`backoffice/src/core/billing_grant_rules.py`): active | scheduled | expired
// | revoked.

export const GRANT_TIERS = ["starter", "business", "enterprise"] as const;
export type GrantTier = (typeof GRANT_TIERS)[number];

const STATUS_LABEL: Record<string, string> = {
  active: "Actif",
  scheduled: "Programmé",
  expired: "Expiré",
  revoked: "Révoqué",
};

const STATUS_COLOR: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-800",
  scheduled: "bg-sky-100 text-sky-800",
  expired: "bg-gray-100 text-gray-700",
  revoked: "bg-red-100 text-red-800",
};

export function grantStatusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

export function grantStatusColor(status: string): string {
  return STATUS_COLOR[status] ?? "bg-gray-100 text-gray-700";
}

// Dates are shown in Paris time whatever the admin's machine says.
const DATE_FMT = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "Europe/Paris",
});

const DATE_TIME_FMT = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Paris",
});

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : DATE_FMT.format(d);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : DATE_TIME_FMT.format(d);
}

/** "Illimité" or "jusqu'au 31/12/2026". */
export function formatGrantEnd(endsAt: string | null | undefined): string {
  if (!endsAt) return "Illimité";
  return `jusqu'au ${formatDate(endsAt)}`;
}

/** Whole days left (rounded up), 0 once over, null when unlimited. */
export function daysLeft(endsAt: string | null | undefined, now: Date = new Date()): number | null {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - now.getTime();
  if (Number.isNaN(ms)) return null;
  return ms <= 0 ? 0 : Math.ceil(ms / 86_400_000);
}

function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

/** Tooltip of the "Offert" badge of the organizations list. */
export function grantListBadgeTitle(tier: string | null | undefined, endsAt: string | null | undefined): string {
  const end = endsAt ? formatGrantEnd(endsAt) : "illimité";
  return `Accès offert — ${capitalize(tier ?? "?")} — ${end}`;
}

/**
 * Readable message out of a FastAPI error body: `detail` may be a string, an
 * object `{error, message}` (409 of the grant API) or a pydantic list.
 */
export function apiErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: unknown } | undefined;
    if (first && typeof first.msg === "string") {
      return first.msg.replace(/^Value error, /, "");
    }
    return fallback;
  }
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
}
